import {Component, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {MenuController, NavController} from '@ionic/angular';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {GuardService} from '../../../services/global/guard.service';
import {AdminProductService} from '../../../services/admin/admin-product.service';
import {AdminStoreService} from '../../../services/admin/admin-store.service';
import {GlobalSettingService} from '../../../services/global/global-setting.service';
import {ScannerService} from '../../../services/core/scanner.service';
import {AuthStorageService} from '../../../services/storage/auth-storage.service';
import {DeliveryType} from '../../../types/enums';
import {Store} from '../../../types/models';

interface AdminMenuSection {
  Title: string;
  Hint: string; // roles, which mainly work with it
  Items: AdminMenuItem[];
}

interface AdminMenuItem {
  Title: string;
  Path?: string[]; // below /admin
  Icon?: string;
  IconSrc?: string;
  Access: () => Promise<boolean>;
  Action?: () => Promise<any>;
  Children?: AdminMenuItem[]; // second level (entries of the navigation page of the group)
  Visible?: boolean;
}

/**
 * Content of the side menu of the administration (right, in place of the customer menu): the entries of the admin start page and, as second level,
 * the entries of their navigation pages, each shown with the access of the user (same conditions as the pages).
 * The ion-menu itself is in app.component.html (the split pane needs it as direct child), it is only active on the
 * pages under /admin and on wide screens always visible.
 */
@Component({
  selector: 'app-admin-side-menu',
  templateUrl: './admin-side-menu.component.html',
  styleUrls: ['./admin-side-menu.component.scss'],
})
export class AdminSideMenuComponent implements OnInit, OnDestroy {

  static readonly MENU_ID = 'admin-menu';
  readonly menuId = AdminSideMenuComponent.MENU_ID;

  active = false;
  currentUrl = '';
  openReports = 0;
  expanded: { [title: string]: boolean } = {};
  private loaded = false;
  private selectedStore: Store = null;
  private isTerminal = false;
  private subscription: Subscription;
  private storeSubscription: Subscription;

  private readonly guard = this.guardService;

  // sections by role: each one is shown, when the user may open at least one of its entries (same access checks as the
  // pages), the entries of a group are its second level
  sections: AdminMenuSection[] = [
    {
      Title: 'Tagesgeschäft', Hint: 'Mitarbeiter, Fahrer, Lager', Items: [
        { Title: 'Bestellungen', Path: ['order-list'], Icon: 'cart-outline', Access: () => this.guard.AdminOrderListAccess() },
        {
          Title: 'Aufgaben', Icon: 'reader-outline', Access: () => this.guard.AdminTaskNavigationAccess(), Children: [
            { Title: 'Anstehende Termine', Path: ['open-time-slot-element-list'], Icon: 'calendar-outline', Access: async () => this.isAppointmentStore() && await this.guard.AdminOpenTimeSlotElementListAccess() },
            { Title: 'Offene Termine', Path: ['open-pending-time-slot-element-list'], Icon: 'hourglass-outline', Access: async () => this.isAppointmentStore() && await this.guard.AdminOpenPendingTimeSlotElementListAccess() },
            { Title: 'Mein Termin', Path: ['employee-my-task', 'open-time-slot-edit'], Icon: 'person-outline', Access: async () => this.isAppointmentStore() },
            { Title: 'Mitarbeiter', Path: ['employee-list'], Icon: 'people-outline', Access: () => this.guard.AdminEmployeeListAccess() },
            { Title: 'Auslieferungs-Simulator', Path: ['delivery-simulator'], Icon: 'flask-outline', Access: () => this.guard.AdminDeliverySimulatorAccess() }
          ]
        },
        {
          Title: 'Lager', Icon: 'cube-outline', Access: () => this.guard.AdminStockNavigationAccess(), Children: [
            { Title: 'Lagerbestand', Path: ['stock-list'], Icon: 'cube-outline', Access: () => this.guard.AdminStockListAccess() },
            { Title: 'Lagerorte', Path: ['inventory-location-list'], Icon: 'grid-outline', Access: () => this.guard.AdminInventoryLocationListAccess() },
            { Title: 'Bestellung', Path: ['stock-order-navigation'], Icon: 'cart-outline', Access: async () => !!this.selectedStore?.Warehousing },
            { Title: 'Wareneingang', Icon: 'arrow-down-circle-outline', Access: async () => !!this.selectedStore?.Warehousing, Action: () => this.scanEan('/admin/stock-incoming-edit', 'Wareneingang') },
            { Title: 'Abschreibung', Icon: 'trash-outline', Access: () => this.guard.AdminStockLossCreateAccess(), Action: () => this.scanEan('/admin/stock-loss-create', 'Abschreibung') },
            { Title: 'Retouren', Icon: 'return-down-back-outline', Access: async () => !!this.selectedStore?.Warehousing, Action: () => this.scanEan('/admin/stock-return-edit', 'Retouren') }
          ]
        },
        { Title: 'Drucker', Path: ['printer'], Icon: 'print-outline', Access: () => this.guard.AdminPrinterAccess() },
        {
          Title: 'Terminals', Icon: 'tv-outline', Access: () => this.guard.AdminTerminalNavigationAccess(), Children: [
            { Title: 'Terminal Standorte', Path: ['terminal-group-list'], Icon: 'location-outline', Access: async () => true },
            { Title: 'Terminal Liste', Path: ['terminal-list'], Icon: 'tv-outline', Access: async () => true },
            { Title: 'Terminal Information', Path: ['terminal-detail'], Icon: 'information-circle-outline', Access: async () => this.isTerminal },
            { Title: 'Code Scannen', Icon: 'scan-outline', Access: async () => !this.isTerminal, Action: () => this.scanner.openScanner('', { title: 'Code Scannen', textTransform: 'toUpperCase' }) }
          ]
        }
      ]
    },
    {
      Title: 'Filialleitung', Hint: 'Filiale, Termine, Team', Items: [
        { Title: 'Filialen', Path: ['store-list'], IconSrc: 'assets/icons/kiwi-marker.svg', Access: () => this.guard.AdminStoreEditAccess() },
        { Title: 'Sortiment der Filiale', Path: ['inventory-list'], IconSrc: 'assets/icons/kiwi-cog.svg', Access: () => this.guard.AdminInventoryListAccess() },
        { Title: 'Öffnungszeiten', Path: ['opening-hours-edit'], Icon: 'timer-outline', Access: () => this.guard.AdminOpeningHoursEditAccess() },
        {
          Title: 'Termine', Icon: 'calendar-number-outline', Access: async () => (await this.guard.AdminTimeSlotListAccess()) || (await this.guard.AdminTimeSlotTypeListAccess()), Children: [
            { Title: 'Termine', Path: ['time-slot-list'], Icon: 'calendar-number-outline', Access: () => this.guard.AdminTimeSlotListAccess() },
            { Title: 'Termin Vorlagen', Path: ['time-slot-type-list'], Icon: 'documents-outline', Access: () => this.guard.AdminTimeSlotTypeListAccess() }
          ]
        },
        { Title: 'Berechtigungen', Path: ['role-list'], IconSrc: 'assets/icons/kiwi-user.svg', Access: () => this.guard.AdminRoleListAccess() },
        {
          Title: 'Nachrichten', Icon: 'newspaper-outline', Access: () => this.guard.AdminNewsNavigationAccess(), Children: [
            { Title: 'Aktuelle Nachrichten (Filiale)', Path: ['store-news-list'], Icon: 'storefront-outline', Access: () => this.guard.AdminStoreNewsListAccess() },
            { Title: 'Aktuelle Nachrichten (Alle)', Path: ['news-list'], Icon: 'newspaper-outline', Access: () => this.guard.AdminNewsEditAccess() },
            { Title: 'Abgearbeitete Nachrichten', Path: ['done-news-list'], Icon: 'checkmark-done-outline', Access: () => this.guard.AdminNewsEditAccess() }
          ]
        },
        { Title: 'Statistiken', Path: ['statistic-navigation'], Icon: 'bar-chart-outline', Access: () => this.guard.AdminStatisticNavigationAccess() }
      ]
    },
    {
      Title: 'Sortiment & Produktdaten', Hint: 'Datenpflege', Items: [
        { Title: 'Produkte', Path: ['product-list'], IconSrc: 'assets/icons/kiwi-bag.svg', Access: () => this.guard.AdminProductEditAccess() },
        { Title: 'Produkte vervollständigen', Path: ['product-completion'], Icon: 'checkmark-done-circle-outline', Access: () => this.guard.AdminProductEditAccess() },
        { Title: 'Produkte anreichern', Path: ['product-database-fill'], Icon: 'library-outline', Access: () => this.guard.AdminProductEditAccess() },
        { Title: 'Fehlermeldungen', Path: ['product-report-review'], Icon: 'flag-outline', Access: () => this.guard.AdminProductEditAccess() },
        {
          Title: 'Kategorien', IconSrc: 'assets/icons/kiwi-paper.svg', Access: () => this.guard.AdminCategoryEditAccess(), Children: [
            { Title: 'Kategorien', Path: ['category-list'], IconSrc: 'assets/icons/kiwi-paper.svg', Access: () => this.guard.AdminCategoryEditAccess() },
            { Title: 'Unterkategorien ordnen', Path: ['category-subcategories'], Icon: 'layers-outline', Access: () => this.guard.AdminCategoryEditAccess() },
            { Title: 'Warengruppen einordnen', Path: ['category-structure'], Icon: 'git-network-outline', Access: () => this.guard.AdminCategoryEditAccess() }
          ]
        },
        { Title: 'Saisons', Path: ['season-list'], Icon: 'calendar-outline', Access: () => this.guard.AdminProductEditAccess() },
        { Title: 'Hersteller', Path: ['producer-list'], Icon: 'business-outline', Access: () => this.guard.AdminProductEditAccess() },
        { Title: 'Nährwerte', Path: ['nutrition-score-list'], IconSrc: 'assets/icons/categories/vegan.svg', Access: () => this.guard.AdminNutritionScoreEditAccess() },
        { Title: 'Suchbegriffe', Path: ['search-list'], IconSrc: 'assets/icons/kiwi-search.svg', Access: () => this.guard.AdminSearchEditAccess() },
        { Title: 'Produktdatenbanken', Path: ['product-database-setting'], Icon: 'server-outline', Access: () => this.guard.AdminProductEditAccess() },
        {
          Title: 'Rezepte', IconSrc: 'assets/icons/kiwi-chef.svg', Access: () => this.guard.AdminRecipeEditAccess(), Children: [
            { Title: 'Rezepte', Path: ['recipe-list'], IconSrc: 'assets/icons/kiwi-chef.svg', Access: () => this.guard.AdminRecipeEditAccess() },
            { Title: 'Rezepte Import', Path: ['recipe-import-navigation'], Icon: 'download-outline', Access: () => this.showRecipeImport() }
          ]
        }
      ]
    },
    {
      Title: 'Marketing', Hint: 'Angebote & Aktionen', Items: [
        {
          Title: 'Angebote', Icon: 'pricetags-outline', Access: () => this.guard.AdminOfferNavigationAccess(), Children: [
            { Title: 'Aktive & kommende Angebote', Path: ['offer-list'], Icon: 'pricetags-outline', Access: async () => true },
            { Title: 'Abgelaufene Angebote', Path: ['offer-ended-list'], Icon: 'time-outline', Access: async () => true }
          ]
        },
        {
          Title: 'Gutscheine', Icon: 'pricetag-outline', Access: () => this.guard.AdminCouponNavigationAccess(), Children: [
            { Title: 'Aktive Gutscheine', Path: ['coupon-list'], Icon: 'pricetag-outline', Access: async () => true },
            { Title: 'Abgelaufene Gutscheine', Path: ['coupon-expired-list'], Icon: 'time-outline', Access: async () => true }
          ]
        },
        { Title: 'Gewinnspiele', Path: ['competition-list'], Icon: 'gift-outline', Access: () => this.guard.AdminCompetitionEditAccess() }
      ]
    },
    {
      Title: 'Buchhaltung', Hint: 'Rechnungen & Zahlungen', Items: [
        { Title: 'Fehlende Rechnungen', Path: ['missing-invoice-list'], Icon: 'receipt-outline', Access: () => this.guard.AdminMissingInvoiceListAccess() },
        { Title: 'Offene Posten', Path: ['open-item-list'], Icon: 'wallet-outline', Access: () => this.guard.AdminMissingInvoiceListAccess() }
      ]
    },
    {
      Title: 'Administration', Hint: 'nur Administratoren', Items: [
        { Title: 'Benutzer', Path: ['user-list'], Icon: 'people-outline', Access: () => this.guard.AdminUserListAccess() },
        { Title: 'Health-Check', Path: ['health-check'], Icon: 'heart-circle-outline', Access: () => this.guard.AdminHealthCheckAccess() }
      ]
    }
  ];

  // all entries of the first level (access, expanded groups)
  get items(): AdminMenuItem[] {
    return this.sections.reduce((all, section) => all.concat(section.Items), [] as AdminMenuItem[]);
  }

  // sections with at least one visible entry
  get visibleSections(): AdminMenuSection[] {
    return this.sections.filter(section => section.Items.some(item => item.Visible));
  }

  toggleSection(section: AdminMenuSection) {
    this.collapsedSections[section.Title] = !this.collapsedSections[section.Title];
    try {
      localStorage.setItem('admin-menu-collapsed', JSON.stringify(this.collapsedSections));
    } catch (e) {
      // without storage the state is only kept until the reload
    }
  }

  // collapsed sections (kept per device)
  collapsedSections: { [title: string]: boolean } = (() => {
    try {
      return JSON.parse(localStorage.getItem('admin-menu-collapsed') || '{}') || {};
    } catch (e) {
      return {};
    }
  })();

  constructor(
    private router: Router,
    private navController: NavController,
    private menuController: MenuController,
    private guardService: GuardService,
    private adminProductService: AdminProductService,
    private storeService: AdminStoreService,
    private globalSettingService: GlobalSettingService,
    private scanner: ScannerService,
    private authStorage: AuthStorageService
  ) { }

  ngOnInit() {
    this.update(this.router.url);
    this.subscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.update(event.urlAfterRedirects));
    // the entries depend on the selected store (e.g. appointments): it may be selected or changed after the menu was
    // loaded, e.g. by the store selection of a page right after the start of the app
    this.storeSubscription = this.authStorage.EmployeeStore.subscribe(() => {
      if (this.active) {
        this.loadAccess();
      } else {
        this.loaded = false;
      }
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }

  // the menu is only active in the administration, its entries are loaded, whenever the administration is entered
  // (another user or store may have been selected meanwhile)
  private async update(url: string) {
    this.currentUrl = url || '';
    const wasActive = this.active;
    this.active = this.currentUrl === '/admin' || this.currentUrl.startsWith('/admin/') || this.currentUrl.startsWith('/admin?');
    if (this.active && (!wasActive || !this.loaded)) {
      this.loaded = true;
      await this.loadAccess();
    }
    // the group and the section of the open page are expanded
    for (const item of this.items) {
      if (item.Children && item.Children.some(child => this.isSelected(child))) {
        this.expanded[item.Title] = true;
      }
    }
    for (const section of this.sections) {
      if (section.Items.some(item => this.isSelected(item) || this.isGroupSelected(item))) {
        this.collapsedSections[section.Title] = false;
      }
    }
  }

  async loadAccess() {
    this.selectedStore = await this.storeService.getSelectedStore().catch(() => null);
    this.isTerminal = await this.authStorage.isTerminal().catch(() => false);
    for (const item of this.items) {
      item.Visible = await this.check(item);
      for (const child of item.Children || []) {
        child.Visible = item.Visible && await this.check(child);
      }
    }
    if (this.items.find(item => item.Path?.[0] === 'product-report-review')?.Visible) {
      this.openReports = (await this.adminProductService.countOpenReports().catch(() => 0)) || 0;
    }
  }

  private async check(item: AdminMenuItem): Promise<boolean> {
    try {
      return !!(await item.Access());
    } catch (e) {
      return false;
    }
  }

  private isAppointmentStore(): boolean {
    return this.selectedStore?.Type === DeliveryType.APPOINTMENT;
  }

  // recipe import: access and at least one import source switched on
  private async showRecipeImport(): Promise<boolean> {
    if (!await this.guard.AdminRecipeImportAccess()) {
      return false;
    }
    const setting = await this.globalSettingService.getRecipeImportSetting();
    return Object.keys(setting || {}).some(key => !!setting[key]);
  }

  visible(items: AdminMenuItem[]): AdminMenuItem[] {
    return (items || []).filter(item => item.Visible);
  }

  isSelected(item: AdminMenuItem): boolean {
    if (!item.Path) {
      return false;
    }
    const path = '/admin/' + item.Path.join('/');
    return this.currentUrl === path || this.currentUrl.startsWith(path + '/') || this.currentUrl.startsWith(path + '?');
  }

  isGroupSelected(item: AdminMenuItem): boolean {
    return !!item.Children && item.Children.some(child => this.isSelected(child));
  }

  title(item: AdminMenuItem): string {
    if (item.Path?.[0] === 'product-report-review' && this.openReports > 0) {
      return item.Title + ' (' + this.openReports + ')';
    }
    return item.Title;
  }

  toggle(item: AdminMenuItem) {
    this.expanded[item.Title] = !this.expanded[item.Title];
  }

  async open(item: AdminMenuItem) {
    if (item.Children) {
      return this.toggle(item);
    }
    await this.menuController.close(this.menuId);
    if (item.Action) {
      return item.Action();
    }
    return this.router.navigate(['/admin', ...item.Path]);
  }

  // start page of the administration (product list)
  async home() {
    await this.menuController.close(this.menuId);
    return this.navController.navigateRoot(['/admin']);
  }

  // former start page with all entries
  async overview() {
    await this.menuController.close(this.menuId);
    return this.navController.navigateRoot(['/admin', 'overview']);
  }

  async leave() {
    await this.menuController.close(this.menuId);
    return this.navController.navigateRoot(['/product-category-list']);
  }

  async profile() {
    await this.menuController.close(this.menuId);
    return this.navController.navigateRoot(['/profile']);
  }

  private async scanEan(url: string, title: string) {
    return this.scanner.openScanner(url, { title, showInput: true, textTransform: 'toUpperCase' });
  }
}
